import { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';
import { useAdminApi, useFilteredData, useRelationshipState, useOrganizationModalState, useAssignRootAdminModalState, useDirectorySearchState, useProcessingState } from './hooks';
import { 
    handleAddMapping, 
    handleRemoveMapping,
    handleToggleAdmin,
    handleDeleteUser,
    handleCreateOrganization,
    handleDeleteOrganization,
    handleStartAssignRootAdmin,
    handleAssignRootAdmin
} from './utils';
import { AdminHeader, LoadingSpinner, EmptyState, AutocompleteInput, SearchBar, PaginationControls, UserRow, OrganizationRow, UsersTable, OrganizationsTable, RelationshipSplitPane, CreateOrganizationModal, AssignRootAdminModal } from './components';

export default function AdminDashboard({ tab = 'relationships' }) {
    const {
        users,
        setUsers,
        organizations,
        setOrganizations,
        accessRequests,
        isLoadingUsers,
        isLoadingOrganizations,
        fetchUsers,
        fetchOrganizations,
        fetchAccessRequests
    } = useAdminApi();
    const [directoryUserId, setDirectoryUserId] = useState(null);
    const [directoryOrgSearch, setDirectoryOrgSearch] = useState('');
    const [directoryOrgOpen, setDirectoryOrgOpen] = useState(false);
    const [requestAction, setRequestAction] = useState(null);
    const [organizationKeys, setOrganizationKeys] = useState({});
    
    // Directory search and pagination state
    const {
        userSearch,
        setUserSearch,
        organizationSearch,
        setOrganizationSearch,
        usersPage,
        setUsersPage,
        organizationsPage,
        setOrganizationsPage,
        ITEMS_PER_PAGE
    } = useDirectorySearchState();
    
    // Relationship split pane state
    const {
        relViewMode,
        setRelViewMode,
        leftSearch,
        setLeftSearch,
        selectedId,
        setSelectedId,
        searchAssign,
        setSearchAssign,
        isAssignDropdownOpen,
        setIsAssignDropdownOpen
    } = useRelationshipState();

    // Processing state
    const {
        processingCell,
        setProcessingCell,
        processingAction,
        setProcessingAction
    } = useProcessingState();
    
    // Modal states
    const {
        isCreateOrganizationOpen,
        setIsCreateOrganizationOpen,
        newOrganization,
        setNewOrganization,
        organizationError,
        setOrganizationError,
        organizationSaving,
        setOrganizationSaving,
        ownerSearch,
        setOwnerSearch,
        isOwnerDropdownOpen,
        setIsOwnerDropdownOpen
    } = useOrganizationModalState();

    // Assign Root Admin modal states
    const {
        isAssignRootAdminOpen,
        setIsAssignRootAdminOpen,
        assignRootAdminOrganization,
        setAssignRootAdminOrganization,
        assignRootAdminUserId,
        setAssignRootAdminUserId,
        assignRootAdminSearch,
        setAssignRootAdminSearch,
        isAssignRootAdminDropdownOpen,
        setIsAssignRootAdminDropdownOpen,
        assignRootAdminError,
        setAssignRootAdminError,
        assignRootAdminSaving,
        setAssignRootAdminSaving
    } = useAssignRootAdminModalState();

    // Filtered data computations
    const {
        filteredUsers,
        filteredOrganizations,
        filteredLeftUsers,
        filteredLeftOrganizations,
        displayedLeftUsers,
        displayedLeftOrganizations,
        paginatedUsers,
        paginatedOrganizations,
        totalUsersPages,
        totalOrganizationsPages,
        filteredOwnerOptions,
        filteredAssignRootAdminOptions,
        selectedUser,
        selectedOrganization,
        selectedEntityName,
        mappedItems,
        filteredAssignOptions
    } = useFilteredData({
        users,
        organizations,
        userSearch,
        organizationSearch,
        leftSearch,
        relViewMode,
        selectedId,
        searchAssign,
        ownerSearch,
        assignRootAdminSearch,
        usersPage,
        organizationsPage,
        ITEMS_PER_PAGE,
        pendingUserIds: accessRequests.map(request => request.userId)
    });

    useEffect(() => {
        setUsersPage(1);
    }, [userSearch]);

    useEffect(() => {
        setOrganizationsPage(1);
    }, [organizationSearch]);

    useEffect(() => {
        fetchUsers();
        fetchOrganizations();
        fetchAccessRequests();
    }, []);

    useEffect(() => {
        setOrganizationKeys(prev => {
            const next = { ...prev };
            for (const request of accessRequests) {
                if (next[request.id] == null) next[request.id] = request.organizationKey || '';
            }
            return next;
        });
    }, [accessRequests]);

    const directoryUser = users.find(user => user.id === directoryUserId) || null;
    const directoryOrgOptions = organizations.filter(org => {
        const mapped = directoryUser?.organizations?.some(item => item.id === org.id);
        if (mapped) return false;
        const query = directoryOrgSearch.toLowerCase();
        return org.id.toLowerCase().includes(query) || org.name.toLowerCase().includes(query);
    });

    const resolveRequest = async (requestId, action, organizationKey) => {
        setRequestAction(requestId);
        const res = await apiFetch(`/api/auth/admin/access-requests/${requestId}/${action}`, {
            method: 'POST',
            headers: action === 'approve' ? { 'Content-Type': 'application/json' } : undefined,
            body: action === 'approve' ? JSON.stringify({ organizationKey }) : undefined
        });
        setRequestAction(null);
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            alert(body.message || 'Unable to update the request.');
            return;
        }
        await Promise.all([fetchAccessRequests(), fetchUsers(), fetchOrganizations()]);
    };

    return (
        <div style={{ padding: '1rem 1.25rem', maxWidth: '1400px', margin: '0 auto', animation: 'fadeIn 0.3s ease', display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%', overflowY: 'auto' }}>
            <AdminHeader
                title="Admin Workspace"
                subtitle="Manage corporate organizations, users mappings, and system credentials."
                breadcrumb={tab === 'relationships' ? 'Relationships Matrix' : tab === 'users' ? 'Users Directory' : 'Organizations Directory'}
                actionButton={tab === 'organizations' && (
                    <button 
                        onClick={() => {
                            setIsCreateOrganizationOpen(true);
                            setOwnerSearch('');
                            setNewOrganization({ id: '', name: '', rootAdminUserId: '' });
                        }}
                        className="btn-primary" 
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', borderRadius: 'var(--radius)', fontSize: '0.9rem' }}
                    >
                        <i className="fa-solid fa-plus"></i> Create Organization
                    </button>
                )}
            />

            {/* TAB 1: RELATIONSHIPS SPLIT PANE */}
            {tab === 'relationships' && (
                <RelationshipSplitPane
                    relViewMode={relViewMode}
                    setRelViewMode={setRelViewMode}
                    leftSearch={leftSearch}
                    setLeftSearch={setLeftSearch}
                    users={users}
                    organizations={organizations}
                    filteredLeftUsers={filteredLeftUsers}
                    filteredLeftOrganizations={filteredLeftOrganizations}
                    displayedLeftUsers={displayedLeftUsers}
                    displayedLeftOrganizations={displayedLeftOrganizations}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    searchAssign={searchAssign}
                    setSearchAssign={setSearchAssign}
                    isAssignDropdownOpen={isAssignDropdownOpen}
                    setIsAssignDropdownOpen={setIsAssignDropdownOpen}
                    handleAddMapping={(targetId) => handleAddMapping(targetId, relViewMode, selectedId, setProcessingCell, setIsAssignDropdownOpen, setSearchAssign, fetchUsers, fetchOrganizations)}
                    filteredAssignOptions={filteredAssignOptions}
                    processingCell={processingCell}
                    handleRemoveMapping={(targetId) => handleRemoveMapping(targetId, relViewMode, selectedId, setProcessingCell, fetchUsers, fetchOrganizations)}
                    selectedEntityName={selectedEntityName}
                    selectedIdForDetails={selectedId}
                    mappedItems={mappedItems}
                />
            )}

            {/* TAB 2: USERS DIRECTORY */}
            {tab === 'users' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
                            Request for access
                        </div>
                        {accessRequests.length === 0 ? (
                            <EmptyState message="No requests waiting for approval." variant="inline" />
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                                        <th style={{ padding: '0.5rem 0.75rem' }}>Name</th>
                                        <th style={{ padding: '0.5rem 0.75rem' }}>Email</th>
                                        <th style={{ padding: '0.5rem 0.75rem' }}>Organization</th>
                                        <th style={{ padding: '0.5rem 0.75rem' }}>Key</th>
                                        <th style={{ padding: '0.5rem 0.75rem' }}>Requested</th>
                                        <th style={{ padding: '0.5rem 0.75rem' }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {accessRequests.map(request => (
                                        <tr key={request.id} style={{ borderTop: '1px solid var(--border)' }}>
                                            <td style={{ padding: '0.5rem 0.75rem' }}>{request.fullName || '—'}</td>
                                            <td style={{ padding: '0.5rem 0.75rem' }}>{request.email}</td>
                                            <td style={{ padding: '0.5rem 0.75rem' }}>{request.requestedOrganizationName}</td>
                                            <td style={{ padding: '0.5rem 0.75rem' }}>
                                                <input
                                                    className="input-field"
                                                    value={organizationKeys[request.id] ?? request.organizationKey}
                                                    onChange={e => setOrganizationKeys(prev => ({ ...prev, [request.id]: e.target.value }))}
                                                    title={request.keyConflicts ? 'This key is already used. Enter a different organization key.' : 'Organization key'}
                                                    aria-label="Organization key"
                                                />
                                            </td>
                                            <td style={{ padding: '0.5rem 0.75rem' }}>{new Date(request.createdAt).toLocaleString()}</td>
                                            <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                                                <button type="button" className="btn-primary" disabled={requestAction === request.id} onClick={() => resolveRequest(request.id, 'approve', organizationKeys[request.id] ?? request.organizationKey)} style={{ marginRight: '0.5rem' }}>Approve</button>
                                                <button type="button" className="btn-secondary" disabled={requestAction === request.id} onClick={() => resolveRequest(request.id, 'reject')}>Reject</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {/* Search */}
                    <SearchBar
                        value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        placeholder="Search users directory by name, email, title, or organization..."
                    />

                    {/* Users Grid */}
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                        <UsersTable
                            isLoading={isLoadingUsers}
                            users={users}
                            filteredUsers={filteredUsers}
                            paginatedUsers={paginatedUsers}
                            currentPage={usersPage}
                            totalPages={totalUsersPages}
                            itemsPerPage={ITEMS_PER_PAGE}
                            processingAction={processingAction}
                            onToggleAdmin={(userId, targetUserName) => handleToggleAdmin(userId, targetUserName, setProcessingAction, fetchUsers)}
                            onDeleteUser={(userId, email) => handleDeleteUser(userId, email, setProcessingAction, fetchUsers, fetchOrganizations, selectedId, setSelectedId)}
                            onPageChange={(page) => setUsersPage(page)}
                            selectedUserId={directoryUserId}
                            onSelectUser={(userId) => {
                                setDirectoryUserId(userId);
                                setDirectoryOrgSearch('');
                                setDirectoryOrgOpen(false);
                            }}
                        />
                    </div>
                    {directoryUser && (
                        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <h3 style={{ margin: 0 }}>{directoryUser.fullName || directoryUser.email}</h3>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className="input-field"
                                    value={directoryOrgSearch}
                                    placeholder="Type to search and select organization..."
                                    onChange={e => { setDirectoryOrgSearch(e.target.value); setDirectoryOrgOpen(true); }}
                                    onFocus={() => setDirectoryOrgOpen(true)}
                                />
                                {directoryOrgOpen && (
                                    <div style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: '200px', overflowY: 'auto' }}>
                                        {directoryOrgOptions.length === 0 ? (
                                            <EmptyState message="No matches found." variant="inline" />
                                        ) : directoryOrgOptions.map(org => (
                                            <button
                                                key={org.id}
                                                type="button"
                                                onClick={() => handleAddMapping(org.id, 'user', directoryUser.id, setProcessingCell, setDirectoryOrgOpen, setDirectoryOrgSearch, fetchUsers, fetchOrganizations)}
                                                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '0.5rem 0.75rem', cursor: 'pointer' }}
                                            >
                                                {org.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {(directoryUser.organizations || []).length === 0 ? (
                                <EmptyState message="No organizations assigned." variant="inline" />
                            ) : directoryUser.organizations.map(org => (
                                <div key={org.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>{org.name}</span>
                                    <button type="button" className="btn-secondary" onClick={() => handleRemoveMapping(org.id, 'user', directoryUser.id, setProcessingCell, fetchUsers, fetchOrganizations)}>Remove</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* TAB 3: TENANTS DIRECTORY */}
            {tab === 'organizations' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {/* Search */}
                    <SearchBar
                        value={organizationSearch}
                        onChange={e => setOrganizationSearch(e.target.value)}
                        placeholder="Search corporate organizations by name or unique ID..."
                    />

                    {/* Organizations Grid */}
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                        <OrganizationsTable
                            isLoading={isLoadingOrganizations}
                            organizations={organizations}
                            filteredOrganizations={filteredOrganizations}
                            paginatedOrganizations={paginatedOrganizations}
                            currentPage={organizationsPage}
                            totalPages={totalOrganizationsPages}
                            itemsPerPage={ITEMS_PER_PAGE}
                            users={users}
                            processingAction={processingAction}
                            onAssignRootAdmin={(organization) => handleStartAssignRootAdmin(organization, setAssignRootAdminOrganization, setAssignRootAdminUserId, setAssignRootAdminSearch, setAssignRootAdminError, setAssignRootAdminSaving, setIsAssignRootAdminOpen, users)}
                            onDeleteOrganization={(organizationId, organizationName) => handleDeleteOrganization(organizationId, organizationName, setProcessingAction, fetchOrganizations, fetchUsers, selectedId, setSelectedId)}
                            onPageChange={(page) => setOrganizationsPage(page)}
                        />
                    </div>
                </div>
            )}

            {/* CREATE ORGANIZATION MODAL */}
            <CreateOrganizationModal
                isOpen={isCreateOrganizationOpen}
                onClose={() => {
                    setIsCreateOrganizationOpen(false);
                    setOwnerSearch('');
                    setNewOrganization({ id: '', name: '', rootAdminUserId: '' });
                }}
                newOrganization={newOrganization}
                setNewOrganization={setNewOrganization}
                organizationError={organizationError}
                setOrganizationError={setOrganizationError}
                organizationSaving={organizationSaving}
                onSubmit={(e) => handleCreateOrganization(e, newOrganization, setNewOrganization, setOrganizationError, setOrganizationSaving, setIsCreateOrganizationOpen, setOwnerSearch, fetchOrganizations, fetchUsers)}
                users={users}
                ownerSearch={ownerSearch}
                setOwnerSearch={setOwnerSearch}
                isOwnerDropdownOpen={isOwnerDropdownOpen}
                setIsOwnerDropdownOpen={setIsOwnerDropdownOpen}
                filteredOwnerOptions={filteredOwnerOptions}
            />

            {/* ASSIGN ROOT ADMINISTRATOR MODAL */}
            <AssignRootAdminModal
                isOpen={isAssignRootAdminOpen}
                onClose={() => setIsAssignRootAdminOpen(false)}
                organization={assignRootAdminOrganization}
                assignRootAdminUserId={assignRootAdminUserId}
                setAssignRootAdminUserId={setAssignRootAdminUserId}
                assignRootAdminSearch={assignRootAdminSearch}
                setAssignRootAdminSearch={setAssignRootAdminSearch}
                isAssignRootAdminDropdownOpen={isAssignRootAdminDropdownOpen}
                setIsAssignRootAdminDropdownOpen={setIsAssignRootAdminDropdownOpen}
                assignRootAdminError={assignRootAdminError}
                setAssignRootAdminError={setAssignRootAdminError}
                assignRootAdminSaving={assignRootAdminSaving}
                onSubmit={(e) => handleAssignRootAdmin(e, assignRootAdminOrganization, assignRootAdminUserId, setAssignRootAdminError, setAssignRootAdminSaving, setIsAssignRootAdminOpen, fetchOrganizations, fetchUsers)}
                users={users}
                filteredAssignRootAdminOptions={filteredAssignRootAdminOptions}
            />
        </div>
    );
}
